import React, { useState, useRef, useEffect } from 'react';
import LiveChatMessage from './LiveChatMessage';
import DemoGuide from './DemoGuide';
import { graphBuilderApi } from '../../services/api';

const LiveChatInterface = ({ settings, persona, scenario }) => {
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [inputMode, setInputMode] = useState('nl'); // 'nl' or 'json'
  const [jsonValue, setJsonValue] = useState('{}');
  const [jsonError, setJsonError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef(null);
  const shouldAutoScroll = useRef(false);

  // Only scroll to bottom when the user sends a message (not during streaming)
  useEffect(() => {
    if (shouldAutoScroll.current) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      shouldAutoScroll.current = false;
    }
  }, [messages]);

  // Validate JSON input
  const validateJson = (value) => {
    try {
      JSON.parse(value);
      setJsonError('');
      return true;
    } catch (e) {
      setJsonError('Invalid JSON: ' + e.message);
      return false;
    }
  };

  // Add a message to the chat
  const addMessage = (type, content, metadata = null) => {
    const message = {
      id: Date.now() + Math.random(),
      type, // 'user', 'cerebral', 'error', 'system'
      content,
      metadata,
      timestamp: new Date().toISOString(),
    };
    setMessages(prev => [...prev, message]);
    return message;
  };

  // Handle send message
  const handleSend = async () => {
    // Validate input
    if (inputMode === 'nl' && !inputValue.trim()) {
      return;
    }
    if (inputMode === 'json' && !validateJson(jsonValue)) {
      return;
    }

    // Build the request payload
    const payload = {
      execution_mode: settings.execution_mode,
      workspace_id: settings.workspace_id || undefined,
      graph_id: settings.graph_id || undefined,
      min_similarity: settings.min_similarity || undefined,
      snn_execution_mode: settings.snn_execution_mode,
      similarity_top_k: settings.similarity_top_k || undefined,
      decompose_query: settings.decompose_query,
      nl_prompt: settings.nl_prompt || undefined,
      include_execution_trace: true,
    };

    // Add user query based on input mode
    if (inputMode === 'nl') {
      payload.nl_query = inputValue.trim();
      shouldAutoScroll.current = true;
      addMessage('user', inputValue.trim());
    } else {
      try {
        const jsonData = JSON.parse(jsonValue);
        // Check if it's an array for bounded_json_array
        if (Array.isArray(jsonData)) {
          payload.bounded_json_array = jsonData;
        } else {
          payload.bounded_json = jsonData;
        }
        addMessage('user', `JSON Input:\n${JSON.stringify(jsonData, null, 2)}`);
      } catch (e) {
        addMessage('error', 'Invalid JSON input');
        return;
      }
    }

    // Clear input
    if (inputMode === 'nl') {
      setInputValue('');
    }

    // Send request
    setIsLoading(true);
    try {
      // P8-5: prefer the SSE streaming endpoint so the user sees the
      // first answer token in roughly Phase 1 + Phase 2 + Phase 6 +
      // Phase 7 + first-token-latency (~400-700 ms) instead of waiting
      // for the full answer (~1100 ms median, 7.7 s tail).
      let streamingAnswer = '';
      let cerebralMessageId = null;
      let finalMetadata = null;
      let pipelineError = null;

      const ensureCerebralMessage = () => {
        if (cerebralMessageId == null) {
          const msg = addMessage('cerebral', streamingAnswer || '');
          cerebralMessageId = msg.id;
        }
      };

      const updateCerebralMessage = (extra = {}) => {
        if (cerebralMessageId == null) {
          ensureCerebralMessage();
        }
        setMessages(prev => prev.map(m =>
          m.id === cerebralMessageId
            ? {
                ...m,
                content: streamingAnswer,
                ...(extra.metadata !== undefined ? { metadata: extra.metadata } : {}),
                ...(extra.score !== undefined ? { score: extra.score } : {}),
                ...(extra.top_outcome_nodes !== undefined
                  ? { top_outcome_nodes: extra.top_outcome_nodes }
                  : {}),
                ...(extra.execution_trace !== undefined
                  ? { execution_trace: extra.execution_trace }
                  : {}),
              }
            : m
        ));
      };

      await graphBuilderApi.runPipelineStream(payload, {
        onProgress: () => {
          // Lightweight progress events; UI doesn't need them today.
        },
        onToken: (chunk) => {
          streamingAnswer += chunk || '';
          ensureCerebralMessage();
          updateCerebralMessage();
        },
        onTokenReplace: (newContent) => {
          streamingAnswer = newContent || '';
          ensureCerebralMessage();
          updateCerebralMessage();
        },
        onRefinedAnswer: (refined) => {
          streamingAnswer = refined || streamingAnswer;
          updateCerebralMessage();
        },
        onComplete: (data) => {
          finalMetadata = data || {};
          if (!streamingAnswer && finalMetadata.natural_language_answer) {
            streamingAnswer = finalMetadata.natural_language_answer;
          }
          ensureCerebralMessage();
          updateCerebralMessage({
            metadata: finalMetadata.metadata,
            score: finalMetadata.score,
            top_outcome_nodes: finalMetadata.top_outcome_nodes,
            execution_trace: finalMetadata.execution_trace || null,
          });
        },
        onError: (err) => {
          pipelineError = err;
        },
      });

      if (pipelineError) {
        throw pipelineError;
      }

      // Synthesise a response object compatible with the legacy code path
      // so ProcessNode handling keeps working.
      const response = {
        natural_language_answer: streamingAnswer || (finalMetadata && finalMetadata.natural_language_answer) || '',
        score: finalMetadata && finalMetadata.score,
        top_outcome_nodes: finalMetadata && finalMetadata.top_outcome_nodes,
        metadata: finalMetadata && finalMetadata.metadata,
      };

      if (response.metadata?.process_node_id || response.metadata?.processNodeId) {
        await handleProcessNodeExecution(response, { id: cerebralMessageId });
      }

    } catch (error) {
      console.error('Pipeline error:', error);
      const errorMessage = error.response?.data?.detail 
        || error.response?.data?.message 
        || error.message 
        || 'An error occurred while processing your request.';
      addMessage('error', errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle ProcessNode execution
  const handleProcessNodeExecution = async (pipelineResponse, parentMessage) => {
    const processNodeId = pipelineResponse.metadata?.process_node_id 
      || pipelineResponse.metadata?.processNodeId;
    
    if (!processNodeId) return;

    addMessage('system', `ProcessNode detected (${processNodeId}). Executing process...`);

    try {
      const processPayload = {
        process_id: processNodeId,
        inputs: {
          nl_query: pipelineResponse.metadata?.original_query,
          pipeline_context: pipelineResponse.metadata?.context,
          ...pipelineResponse.metadata?.process_inputs,
        },
        execution_mode: 'full',
        workspace_id: settings.workspace_id || undefined,
      };

      const processResponse = await graphBuilderApi.executeProcess(processPayload);

      // Add process execution result
      addMessage(
        'cerebral',
        `Process Execution Result:\n${processResponse.final_outputs?.result || JSON.stringify(processResponse.final_outputs, null, 2)}`,
        {
          execution_id: processResponse.execution_id,
          status: processResponse.status,
          completed_nodes: processResponse.completed_nodes,
          progress: processResponse.progress,
        }
      );

    } catch (error) {
      console.error('Process execution error:', error);
      addMessage('error', `Process execution failed: ${error.message}`);
    }
  };

  // Handle clear chat
  const handleClear = () => {
    setMessages([]);
  };

  // Handle key press (Enter to send)
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div className="live-chat-interface">
      {/* Header */}
      <div className="live-chat-header">
        <div className="live-chat-title">
          Chat with Cerebral
          {persona && <span style={{ fontWeight: 'normal', marginLeft: '8px' }}>as {persona.name}</span>}
        </div>
        <button className="live-chat-clear-btn" onClick={handleClear}>
          Clear Chat
        </button>
      </div>

      {/* Messages Area */}
      <div className="live-chat-messages">
        {messages.length === 0 ? (
          <div className="live-chat-empty">
            Start a conversation by typing a query below.
            {scenario && (
              <div style={{ marginTop: '10px', fontSize: '12px' }}>
                Current scenario: {scenario.title}
              </div>
            )}
          </div>
        ) : (
          messages.map(message => (
            <LiveChatMessage key={message.id} message={message} />
          ))
        )}
        
        {isLoading && (
          <div className="live-chat-loading">
            <div className="live-chat-loading-spinner"></div>
            <span>Cerebral is thinking...</span>
          </div>
        )}
        
        <div ref={messagesEndRef} />
      </div>

      {/* Input Area */}
      <div className="live-chat-input-area">
        {/* Input Mode Toggle */}
        <div className="live-chat-input-mode">
          <button
            className={`live-chat-mode-btn ${inputMode === 'nl' ? 'active' : ''}`}
            onClick={() => setInputMode('nl')}
          >
            Natural Language
          </button>
          <button
            className={`live-chat-mode-btn ${inputMode === 'json' ? 'active' : ''}`}
            onClick={() => setInputMode('json')}
          >
            JSON Input
          </button>
        </div>

        {/* Input */}
        {inputMode === 'nl' ? (
          <div className="live-chat-input-wrapper">
            <textarea
              className="live-chat-input"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Type your query here... (Press Enter to send, Shift+Enter for new line)"
              disabled={isLoading}
            />
            <button
              className="live-chat-send-btn"
              onClick={handleSend}
              disabled={isLoading || !inputValue.trim()}
            >
              Send
            </button>
          </div>
        ) : (
          <div>
            <textarea
              className="live-chat-json-input"
              value={jsonValue}
              onChange={(e) => {
                setJsonValue(e.target.value);
                validateJson(e.target.value);
              }}
              placeholder='{"key": "value"} or [{"key": "value"}] for array'
              disabled={isLoading}
            />
            {jsonError && <div className="live-chat-json-error">{jsonError}</div>}
            <div className="live-chat-input-wrapper" style={{ marginTop: '10px' }}>
              <button
                className="live-chat-send-btn"
                onClick={handleSend}
                disabled={isLoading || !!jsonError}
                style={{ marginLeft: 'auto' }}
              >
                Send JSON
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Demo Guide — suggested questions per scenario */}
      <DemoGuide
        scenario={scenario}
        onCopyQuery={(q) => setInputValue(q)}
      />
    </div>
  );
};

export default LiveChatInterface;
